/**
 * Inline CTA button displayed below agent messages when demo_offer skill is active.
 * This button appears directly below the message containing 👇 emoji for maximum visibility.
 *
 * This is a thin wrapper around SearchBarCTAButton that:
 * 1. Automatically uses the first (primary) CTA
 * 2. Provides inline-specific styling
 *
 * Design reference: Figma node 1684-443
 */
import type React from 'react';
import { useMemo } from 'react';
import { getSiteCTAs } from '../config/SiteConfigManager';
import type { CTA } from '../config/types';
import SearchBarCTAButton from './SearchBarCTAButton';

interface InlineDemoCTAButtonProps {
    domain: string;
    isCtaPage?: boolean;
    inlineCtaOptionEnabled?: boolean;
    visitorEmail?: string | null;
    onBookingFlowStart?: (
        ctaId: string,
        redirectUrl: string,
        bookingMessage: string,
        ctaAction?: 'navigate' | 'popup' | 'in-chat'
    ) => void;
}

const InlineDemoCTAButton: React.FC<InlineDemoCTAButtonProps> = ({
    domain,
    isCtaPage,
    inlineCtaOptionEnabled,
    visitorEmail,
    onBookingFlowStart,
}) => {
    // Get the first CTA (primary demo CTA)
    const firstCTA = useMemo(() => {
        const ctas = getSiteCTAs(domain) as CTA[];
        // Intent-gated CTAs (e.g. a help-center link) are never the demo CTA.
        return ctas.find((cta) => !cta.show_on_intents?.length);
    }, [domain]);

    if (!firstCTA) {
        return null;
    }

    return (
        <div className="flex items-center w-full mt-4 mb-2">
            <SearchBarCTAButton
                domain={domain}
                placeholder={firstCTA.placeholder}
                show={true}
                showArrow={true}
                fontWeight="font-bold"
                placement="inline"
                isCtaPage={isCtaPage}
                hadDemoOffer={true}
                inlineCtaOptionEnabled={inlineCtaOptionEnabled}
                visitorEmail={visitorEmail}
                onBookingFlowStart={onBookingFlowStart}
            />
        </div>
    );
};

export default InlineDemoCTAButton;
